import { useRuntimeConfig } from '#imports'

import {
  Identify as AmplitudeIdentify,
  Types as AmplitudeTypes,
  flush as amplitudeFlush,
  getDeviceId as amplitudeGetDeviceId,
  getSessionId as amplitudeGetSessionId,
  getUserId as amplitudeGetUserId,
  identify as amplitudeIdentify,
  init as amplitudeInit,
  logEvent as amplitudeLogEvent,
  setOptOut as amplitudeSetOptOut,
  setTransport as amplitudeSetTransport,
  setUserId as amplitudeSetUserId,
} from '@amplitude/analytics-browser'
import { useIsE2ETestingEnvironment } from '@backmarket/nuxt-module-qa/useIsE2ETestingEnvironment'

import { pushEventToDataLayer } from '../google-tag-manager'

import type { UserProperties } from './types'

// BM is under the "growth plan" of Amplitude
// Documentation: https://amplitude.github.io/Amplitude-JavaScript/

const isUnitTest = process.env.VITEST === 'true'

function isTestingEnvironment() {
  const isE2ETestingEnvironment = useIsE2ETestingEnvironment()

  return isUnitTest || isE2ETestingEnvironment
}

export const sendEvent = (eventName: string, payload: object) => {
  if (isTestingEnvironment()) return

  amplitudeLogEvent(eventName, payload)
}

export function setUserProperties(userProperties: UserProperties) {
  if (isTestingEnvironment()) return

  const identify = new AmplitudeIdentify()

  Object.keys(userProperties).forEach((propertyName) => {
    identify.set(propertyName, userProperties[propertyName])
  })

  amplitudeIdentify(identify)
}

export const setUserId = (userId: string) => {
  if (isTestingEnvironment()) return

  amplitudeSetUserId(userId)
}

export const getDeviceId = () => {
  if (isTestingEnvironment()) return ''

  return amplitudeGetDeviceId()
}

export type InitAmplitudeParameters = {
  apiKey: string
  version: string
}

export function init() {
  const {
    public: { tracking: config },
  } = useRuntimeConfig()

  amplitudeInit(config.amplitude.apiKey, {
    appVersion: config.version,
    flushIntervalMillis: 5000,
    logLevel: AmplitudeTypes.LogLevel.None,

    autocapture: {
      attribution: true,
      pageViews: false,
      sessions: true,
      formInteractions: false,
      fileDownloads: false,
      elementInteractions: false,
    },

    // serverZone is fixed to EU in order to keep the data out of the US.
    serverZone: 'EU',
  }).promise.then(() => {
    pushAmplitudeEventToGTM()
  })

  // this function is invoked when page is unloaded
  // switches to beacon and sends all unsent events immediately
  // see https://www.docs.developers.amplitude.com/data/sdks/typescript-browser/migration/?h=onexitpage#flush-or-onexitpage
  window.addEventListener('pagehide', () => {
    // Set https transport to use sendBeacon API
    amplitudeSetTransport('beacon')
    // Send all pending events to server
    amplitudeFlush()
  })
}

function pushAmplitudeEventToGTM() {
  setTimeout(() => {
    const amplitudeEvent = {
      amplitude_device_id: amplitudeGetDeviceId(),
      amplitude_user_id: amplitudeGetUserId(),
      amplitude_session_id: amplitudeGetSessionId(),
      event: 'amplitude',
    }

    pushEventToDataLayer(amplitudeEvent)
  }, 0)
}

export const setOptIn = (optIn: boolean) => {
  if (isTestingEnvironment()) return

  if (optIn) init()

  amplitudeSetOptOut(!optIn)
}
